/**
 * Shared constants used across the platform.
 */

/** Default subscription plan definitions */
export const DEFAULT_PLANS = {
  Basic: {
    name: 'Basic',
    maxServers: 5,
    maxUsers: 3,
    monthlyPrice: 49,
    yearlyPrice: 470,
  },
  Premium: {
    name: 'Premium',
    maxServers: 15,
    maxUsers: 10,
    monthlyPrice: 149,
    yearlyPrice: 1430,
  },
  Gold: {
    name: 'Gold',
    maxServers: 50,
    maxUsers: 25,
    monthlyPrice: 399,
    yearlyPrice: 3830,
  },
  Platinum: {
    name: 'Platinum',
    maxServers: -1, // unlimited
    maxUsers: -1, // unlimited
    monthlyPrice: 999,
    yearlyPrice: 9590,
  },
} as const;

/** Yearly discount percentage (20%) */
export const YEARLY_DISCOUNT_PERCENT = 20;

/** Authentication constants */
export const AUTH = {
  /** Maximum login attempts per IP per minute */
  MAX_LOGIN_ATTEMPTS_PER_MINUTE: 5,
  /** Minimum password length */
  MIN_PASSWORD_LENGTH: 12,
  /** Default JWT expiration (in seconds) */
  DEFAULT_TOKEN_EXPIRY_SECONDS: 3600,
  /** Default session inactivity timeout (in seconds) */
  SESSION_INACTIVITY_TIMEOUT_SECONDS: 1800,
  /** Maximum concurrent sessions per user */
  MAX_CONCURRENT_SESSIONS: 5,
  /** Invitation link expiry (in hours) */
  INVITATION_EXPIRY_HOURS: 72,
  /** Email verification link expiry (in hours) */
  VERIFICATION_EXPIRY_HOURS: 24,
} as const;

/** SSH connection constants */
export const SSH = {
  /** Default connection timeout (ms) */
  DEFAULT_TIMEOUT_MS: 30000,
  /** Initial reconnection delay (ms) */
  INITIAL_BACKOFF_MS: 1000,
  /** Maximum reconnection delay (ms) */
  MAX_BACKOFF_MS: 60000,
  /** Default max connections per server */
  DEFAULT_MAX_POOL_SIZE: 3,
} as const;

/** Metric collection constants */
export const METRICS = {
  /** Default polling interval (seconds) */
  DEFAULT_POLLING_INTERVAL_SECONDS: 60,
  /** Number of top processes to collect */
  TOP_PROCESSES_COUNT: 10,
  /** Default partition retention (days) */
  DEFAULT_RETENTION_DAYS: 90,
  /** Partitions to create ahead (days) */
  PARTITIONS_AHEAD_DAYS: 7,
} as const;

/** Alert engine constants */
export const ALERTS = {
  /** Default evaluation period (seconds) */
  DEFAULT_EVALUATION_PERIOD_SECONDS: 300,
  /** Default cooldown period (seconds) */
  DEFAULT_COOLDOWN_SECONDS: 300,
} as const;

/** File upload constants */
export const UPLOADS = {
  /** Maximum PEM key file size (bytes) */
  MAX_PEM_FILE_SIZE_BYTES: 10240, // 10KB
  /** Minimum RSA key size (bits) */
  MIN_RSA_KEY_BITS: 2048,
} as const;

/** Data export constants */
export const EXPORTS = {
  /** Maximum exports per hour per company */
  MAX_EXPORTS_PER_HOUR: 5,
  /** Default backup retention (days) */
  BACKUP_RETENTION_DAYS: 30,
} as const;

/** Database provisioning constants */
export const DATABASE = {
  /** Maximum database name length */
  MAX_DB_NAME_LENGTH: 64,
} as const;

/** Billing constants */
export const BILLING = {
  /** Grace period for failed payments (days) */
  FAILED_PAYMENT_GRACE_DAYS: 7,
  /** Maximum payment retries */
  MAX_PAYMENT_RETRIES: 3,
} as const;

/** HTTP polling fallback constants */
export const POLLING = {
  /** Default polling interval when WebSocket is unavailable (ms) */
  DEFAULT_INTERVAL_MS: 10000,
  /** Maximum polling interval with backoff (ms) */
  MAX_INTERVAL_MS: 60000,
  /** Time to wait before considering WebSocket connection failed (ms) */
  WS_CONNECT_TIMEOUT_MS: 5000,
  /** Number of WebSocket reconnection attempts before falling back to polling */
  WS_MAX_RECONNECT_ATTEMPTS: 3,
} as const;
